import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { handleTalkToSpecialist } from '../utils/webchat';
import './SharedResult.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.libbenagents.com';

// Renderiza markdown simples de forma segura — sem dangerouslySetInnerHTML
function SimpleMarkdown({ text }) {
  const lines = text.split('\n');
  return (
    <div className="sr-content">
      {lines.map((line, i) => {
        if (line.startsWith('- ')) return <li key={i}>{line.slice(2)}</li>;
        if (line.startsWith('## ')) return <h3 key={i}>{line.slice(3)}</h3>;
        if (line.startsWith('# ')) return <h2 key={i}>{line.slice(2)}</h2>;
        if (line === '') return <br key={i} />;
        const parts = line.split(/\*\*(.+?)\*\*/);
        return (
          <p key={i}>
            {parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}
          </p>
        );
      })}
    </div>
  );
}

export default function SharedResult() {
  const { shareId }   = useParams();
  const { t, i18n }   = useTranslation();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/shares/${shareId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [shareId]);

  const share = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: data?.title, url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <div className="sr-loading">{t('sharedResult.loading')}</div>;
  if (!data)   return (
    <div className="sr-loading">
      <p>{t('sharedResult.expired')}</p>
      <a href="/">{t('sharedResult.backHome')}</a>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{data.title} | Libben AI</title>
        <meta name="description" content={t('sharedResult.metaDesc', { agent: data.agentName, defaultValue: `Resultado gerado por ${data.agentName} via Libben AI` })} />
        <meta property="og:title" content={data.title} />
        <meta property="og:description" content={t('sharedResult.metaDesc', { agent: data.agentName, defaultValue: `Resultado gerado por ${data.agentName}` })} />
        <html lang={i18n.language} />
      </Helmet>

      <div className="sr-page">
        <div className="sr-card">
          <div className="sr-header">
            <div className="sr-agent">
              <span className="sr-agent-icon">{data.agentIcon}</span>
              <div>
                <div className="sr-agent-name">{data.agentName}</div>
                <div className="sr-agent-label">{t('sharedResult.generatedBy')}</div>
              </div>
            </div>
            <button className="sr-share-btn" onClick={share}>
              {copied ? t('sharedResult.copied') : t('sharedResult.shareBtn')}
            </button>
          </div>

          <h1 className="sr-title">{data.title}</h1>
          <SimpleMarkdown text={data.content} />

          <div className="sr-meta">
            {new Date(data.createdAt).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'pt-BR', {
              day: '2-digit', month: 'long', year: 'numeric'
            })}
          </div>
        </div>

        <div className="sr-cta">
          <p>{t('sharedResult.ctaText')}</p>
          <button type="button" onClick={handleTalkToSpecialist} className="sr-cta-btn">
            {t('sharedResult.ctaBtn')}
          </button>
          <span className="sr-cta-note">{t('sharedResult.ctaNote')}</span>
        </div>
      </div>
    </>
  );
}
